import {
  quoteParametersStore,
  setLtlQuoteState,
  setQuoteFormStore,
} from '@store/ltl/store';
import {
  fetchLtlQuoteAccessorials,
  fetchLtlQuoteLoadItems,
} from '@store/orders/services';
import { LtlQuoteViewModelLegacy } from '@store/orders/types';
import { toDimensionValue } from '@utils/ltl/dimensions';
import { printError } from '@utils/utils';

import { LTLHazmatDetails, LTLLoadItem } from './types';

function requestQuoteFormReset() {
  setQuoteFormStore('resetForm', true);
}

function acknowledgeReset() {
  setQuoteFormStore('resetForm', false);
}

/**
 * Fetches quote data by parameters ID and sets it in the LTL quote state
 * @param quoteParametersId The ID of the quote parameters
 */
async function fetchQuoteDataByParametersId(quoteParametersId: number) {
  try {
    const selectedQuoteParameters = quoteParametersStore.quoteParameters.find(
      (qp) => qp.id === quoteParametersId,
    );

    // Set the quote data in the store
    setLtlQuoteState('requote', true);

    if (selectedQuoteParameters) {
      // Fetch both load items and accessorials in parallel
      const [loadItems, accessorials] = await Promise.all([
        fetchLtlQuoteLoadItems(quoteParametersId),
        fetchLtlQuoteAccessorials(quoteParametersId),
      ]);

      const mappedLoadItems = loadItems.map((item): LTLLoadItem => {
        const defaultUnitType = item.item || 'Pallets';

        // Map the quoteLoadItemBreakdown to ensure proper type compatibility
        const mappedBreakdown = item.quoteLoadItemBreakdown
          ? item.quoteLoadItemBreakdown.map((breakdown) => ({
              ...breakdown,
              nmfc: breakdown.nmfc ?? null,
              description: breakdown.description ?? '',
              weight: breakdown.weight ?? null,
              pieces: breakdown.pieces?.toString() ?? null,
              class: breakdown.class ?? null,
              hazmat: breakdown.hazmat || false,
              quoteHazmatDetail: breakdown.quoteHazmatDetail ?? null,
              loadItemHazmatDetail: (breakdown.loadItemHazmatDetail ??
                breakdown.quoteHazmatDetail ??
                null) as LTLHazmatDetails | null,
            }))
          : [];

        return {
          ...item,
          class: Number(item.class).toString(),
          item: item.unitType ?? defaultUnitType,
          type: item.type ?? defaultUnitType,
          heightInch: toDimensionValue(item.height),
          lengthInch: toDimensionValue(item.length),
          widthInch: toDimensionValue(item.width),
          height: null,
          length: null,
          width: null,
          pieces: item.pieces ?? null,
          quantity: item.quantity,
          weight: item.weight ?? null,
          uom: 'lbs',
          description: item.description ?? '',
          loadItemHazmatDetail: item.quoteHazmatDetail as LTLHazmatDetails,
          suggestedClass: item.suggestedClass ?? null,
          hazmatItems: item.hazmatItems ?? null,
          hazmatDescription: item.hazmatDescription ?? null,
          pcf: item.pcf ?? '',
          quoteLoadItemBreakdown: mappedBreakdown,
          nmfc: item.nmfc ?? null,
          charge: item.charge != null ? String(item.charge) : item.charge,
          hazmat: item.hazmat || false,
        };
      });

      // Process accessorials
      const deliveryAccessorials = accessorials
        .filter(
          (accessorial) => accessorial.accessorialDescription === 'Delivery',
        )
        .map((accessorial) => accessorial.accessorialName);

      const pickupAccessorials = accessorials
        .filter(
          (accessorial) => accessorial.accessorialDescription === 'Pickup',
        )
        .map((accessorial) => accessorial.accessorialName);

      const shipmentAccessorials = accessorials
        .filter((accessorial) => accessorial.isGlobal)
        .map((accessorial) => accessorial.accessorialName);

      // Set the quote state with the mapped data
      setLtlQuoteState('quote', {
        customerName:
          selectedQuoteParameters.customerName &&
          selectedQuoteParameters.customerName !== 'Unassigned'
            ? selectedQuoteParameters.customerName
            : '',
        customerId: selectedQuoteParameters.customerId,
        originZip: selectedQuoteParameters.originZip,
        originState: selectedQuoteParameters.originState,
        originCity: selectedQuoteParameters.originCity,
        originCountry: selectedQuoteParameters.originCountry,
        destinationZip: selectedQuoteParameters.destinationZip,
        destinationCity: selectedQuoteParameters.destinationCity,
        destinationState: selectedQuoteParameters.destinationState,
        destinationCountry: selectedQuoteParameters.destinationCountry,
        loadItems: mappedLoadItems,
        ltlQuote: {
          deliveryAccessorialDescription: deliveryAccessorials.join(', '),
          deliveryAccessorials,
          pickupAccessorialDescription: pickupAccessorials.join(', '),
          pickupAccessorials,
          shipmentAccessorials,
          quoteParametersId,
          rateRequest: {
            isStackable: selectedQuoteParameters.isStackable,
            isLinearFeetOverridden:
              selectedQuoteParameters.isLinearFeetOverridden,
            linearFeet: selectedQuoteParameters.linearFeet.toString(),
          },
          ltlInsurance: {
            shippingSumInsured:
              selectedQuoteParameters.ltlInsuranceQuote?.declaredValue ?? 0,
            quotePremium:
              selectedQuoteParameters.ltlInsuranceQuote?.premium ?? 0,
            quoteServiceFee:
              selectedQuoteParameters.ltlInsuranceQuote?.serviceFee ?? 0,
          },
        } as LtlQuoteViewModelLegacy,
      });

      return selectedQuoteParameters;
    }
    return null;
  } catch (error) {
    printError(error);
    return null;
  }
}

async function requote(quoteParametersId: number) {
  setQuoteFormStore('quoteParametersId', quoteParametersId);
  await fetchQuoteDataByParametersId(quoteParametersId);
}

export {
  acknowledgeReset,
  fetchQuoteDataByParametersId,
  requestQuoteFormReset,
  requote,
};
